(function () {
  const ROOT_SELECTOR = '.drahance-judgeme-reviews';
  const WIDGET_SELECTOR = '#judgeme_product_reviews .jdgm-rev-widg';
  const GOLD = '#C8A165';
  const MEDIA_PAGE_SIZE = 100;
  const mediaStates = new WeakMap();
  const reviewPaginationStates = new WeakMap();
  let mediaModal = null;

  function toNumber(value, fallback = 0) {
    const number = Number.parseFloat(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function formatRating(value) {
    return toNumber(value).toFixed(1);
  }

  function createStars(score) {
    const stars = document.createElement('div');
    stars.className = 'drahance-jdgm-static-stars';
    stars.setAttribute('aria-label', `${formatRating(score)} out of 5 stars`);

    for (let index = 1; index <= 5; index += 1) {
      const star = document.createElement('span');
      star.className = index <= Math.round(toNumber(score)) ? 'is-filled' : 'is-empty';
      star.textContent = '★';
      stars.appendChild(star);
    }

    return stars;
  }

  function createHistogram(widget) {
    const list = document.createElement('div');
    list.className = 'drahance-jdgm-static-histogram';

    for (let rating = 5; rating >= 1; rating -= 1) {
      const source = widget.querySelector(`.jdgm-histogram__row[data-rating="${rating}"]`);
      const percentage = source ? toNumber(source.getAttribute('data-percentage')) : 0;
      const frequency = source ? Number.parseInt(source.getAttribute('data-frequency') || '0', 10) : 0;

      const row = document.createElement('div');
      row.className = 'drahance-jdgm-static-histogram__row';

      const label = document.createElement('span');
      label.className = 'drahance-jdgm-static-histogram__label';
      label.textContent = `${rating} ★`;

      const bar = document.createElement('span');
      bar.className = 'drahance-jdgm-static-histogram__bar';
      const fill = document.createElement('span');
      fill.style.width = `${Math.max(0, Math.min(100, percentage))}%`;
      bar.appendChild(fill);

      const count = document.createElement('span');
      count.className = 'drahance-jdgm-static-histogram__count';
      count.textContent = String(Number.isFinite(frequency) ? frequency : 0);

      row.append(label, bar, count);
      list.appendChild(row);
    }

    return list;
  }

  function getPhotoUrl(node) {
    return (
      node.getAttribute('data-mfp-src') ||
      node.getAttribute('href') ||
      node.getAttribute('data-src') ||
      node.getAttribute('src') ||
      node.querySelector('img')?.getAttribute('data-src') ||
      node.querySelector('img')?.getAttribute('src') ||
      ''
    );
  }

  function getPhotoKey(url) {
    try {
      const parsed = new URL(url, window.location.href);
      return `${parsed.origin}${parsed.pathname}`;
    } catch (error) {
      return url.split(/[?#]/)[0];
    }
  }

  function getMediaKey(type, value) {
    return `${type}:${getPhotoKey(value)}`;
  }

  function getImageMedia(node) {
    const url = getPhotoUrl(node);
    if (!url) return null;

    const image = node.matches('img') ? node : node.querySelector('img');
    const thumbnail = (
      image?.getAttribute('data-src') ||
      image?.getAttribute('src') ||
      url
    );

    return {
      type: 'image',
      url,
      thumbnail,
      alt: image?.getAttribute('alt') || 'Customer review photo',
      key: getMediaKey('image', url),
    };
  }

  function getVideoMedia(node) {
    const youtubeId = node.classList.contains('jdgm-yt-video')
      ? (node.getAttribute('data-id') || '')
      : '';
    const externalId = node.getAttribute('data-external-id') || '';
    const iframe = node.matches('iframe') ? node : node.querySelector('iframe');
    const video = node.matches('video') ? node : node.querySelector('video');
    const source = video?.querySelector('source');
    let iframeUrl = (
      node.getAttribute('data-iframe-src') ||
      iframe?.getAttribute('src') ||
      ''
    );
    const videoUrl = (
      video?.getAttribute('src') ||
      source?.getAttribute('src') ||
      node.getAttribute('data-video-url') ||
      node.getAttribute('data-src') ||
      ''
    );

    if (youtubeId) {
      iframeUrl = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(youtubeId)}?rel=0`;
    } else if (!iframeUrl && externalId) {
      iframeUrl = externalId.length > 16
        ? `https://iframe.videodelivery.net/${encodeURIComponent(externalId)}`
        : `https://player.vimeo.com/video/${encodeURIComponent(externalId)}`;
    }

    if (!iframeUrl && !videoUrl) return null;

    const image = node.querySelector('img');
    let thumbnail = (
      node.getAttribute('data-thumbnail') ||
      node.getAttribute('data-poster') ||
      video?.getAttribute('poster') ||
      image?.getAttribute('data-src') ||
      image?.getAttribute('src') ||
      ''
    );

    if (!thumbnail && youtubeId) {
      thumbnail = `https://img.youtube.com/vi/${encodeURIComponent(youtubeId)}/hqdefault.jpg`;
    } else if (!thumbnail && externalId) {
      thumbnail = externalId.length > 16
        ? `https://cloudflarestream.com/${encodeURIComponent(externalId)}/thumbnails/thumb_5_0.jpg`
        : `https://i.vimeocdn.com/video/${encodeURIComponent(externalId)}_200.jpg`;
    }

    const identity = externalId || youtubeId || iframeUrl || videoUrl;
    return {
      type: 'video',
      url: iframeUrl || videoUrl,
      iframeUrl,
      videoUrl,
      thumbnail,
      alt: node.getAttribute('data-title') || 'Customer review video',
      key: getMediaKey('video', identity),
    };
  }

  function getMediaItems(source) {
    const pictureLinks = Array.from(source.querySelectorAll('.jdgm-rev__pic-link'));
    const standalonePictures = Array.from(source.querySelectorAll('.jdgm-rev__pic-img'))
      .filter((image) => !image.closest('.jdgm-rev__pic-link'));
    const videoPlayers = Array.from(source.querySelectorAll(
      '.jdgm-rev__vids > .jdgm-vid-player, .jdgm-rev__vids > .jdgm-yt-video'
    ));
    const standaloneVideos = Array.from(source.querySelectorAll('.jdgm-rev__vids video, .jdgm-rev__vids iframe'))
      .filter((media) => !media.closest('.jdgm-vid-player, .jdgm-yt-video'));
    const seen = new Set();

    return [...pictureLinks, ...standalonePictures, ...videoPlayers, ...standaloneVideos].reduce((items, node) => {
      const item = node.matches('.jdgm-rev__pic-link, .jdgm-rev__pic-img')
        ? getImageMedia(node)
        : getVideoMedia(node);
      if (!item || seen.has(item.key)) return items;

      seen.add(item.key);
      items.push(item);
      return items;
    }, []);
  }

  function mergeMediaItems(state, items) {
    items.forEach((item) => {
      if (state.keys.has(item.key)) return;
      state.keys.add(item.key);
      state.items.push(item);
    });
  }

  function getMediaState(root, widget) {
    let state = mediaStates.get(root);
    if (!state) {
      state = {
        items: [],
        keys: new Set(),
        loading: false,
        loaded: false,
        failed: false,
      };
      mediaStates.set(root, state);
    }

    const source = widget.querySelector('.jdgm-rev-widg__reviews') || widget;
    mergeMediaItems(state, getMediaItems(source));
    return state;
  }

  function getMediaRequestConfig(widget) {
    const reviewWidget = widget.closest('#judgeme_product_reviews');
    const pagination = widget.querySelector('.jdgm-paginate[data-url]');
    const productId = reviewWidget?.getAttribute('data-product-id') || reviewWidget?.getAttribute('data-id');
    if (!pagination || !productId) return null;

    const shopDomain = window.Shopify?.shop || window.location.hostname;
    const updatedAt = widget.getAttribute('data-updated-at') || '';
    const updatedAtSeconds = updatedAt ? Math.floor(Date.parse(updatedAt) / 1000) : '';

    return {
      endpoint: pagination.getAttribute('data-url'),
      productId,
      shopDomain,
      updatedAtSeconds: Number.isFinite(updatedAtSeconds) ? updatedAtSeconds : '',
    };
  }

  async function fetchMediaPage(config, page) {
    const url = new URL(config.endpoint, window.location.href);
    url.protocol = 'https:';
    url.searchParams.set('url', config.shopDomain);
    url.searchParams.set('shop_domain', config.shopDomain);
    url.searchParams.set('platform', window.jdgmSettings?.platform || 'shopify');
    url.searchParams.set('product_id', config.productId);
    url.searchParams.set('sort_by', 'with_media');
    url.searchParams.set('page', String(page));
    url.searchParams.set('per_page', String(MEDIA_PAGE_SIZE));
    if (config.updatedAtSeconds) url.searchParams.set('ts', String(config.updatedAtSeconds));

    const response = await fetch(url.toString(), {
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`Judge.me media request failed: ${response.status}`);
    return response.json();
  }

  function getMediaFromResponse(response) {
    if (!response?.html) return [];
    const documentFragment = new DOMParser().parseFromString(response.html, 'text/html');
    return getMediaItems(documentFragment);
  }

  async function fetchAllProductMedia(config) {
    const firstPage = await fetchMediaPage(config, 1);
    const totalMediaReviews = Number.parseInt(firstPage.total_count || '0', 10);
    const pageCount = Math.max(1, Math.ceil(totalMediaReviews / MEDIA_PAGE_SIZE));
    const responses = [firstPage];

    for (let start = 2; start <= pageCount; start += 3) {
      const requests = [];
      for (let page = start; page < start + 3 && page <= pageCount; page += 1) {
        requests.push(fetchMediaPage(config, page));
      }
      responses.push(...await Promise.all(requests));
    }

    return responses.flatMap(getMediaFromResponse);
  }

  function getReviewPaginationState(root) {
    let state = reviewPaginationStates.get(root);
    if (!state) {
      state = {
        cache: new Map(),
        loading: false,
      };
      reviewPaginationStates.set(root, state);
    }
    return state;
  }

  function getReviewSort(widget) {
    const sort = widget.querySelector('.jdgm-sort-dropdown');
    if (!sort) return '';
    return sort.value || sort.getAttribute('data-sort-by') || '';
  }

  function getReviewPaginationDetails(widget) {
    const pagination = widget.querySelector('.jdgm-paginate[data-url]');
    if (!pagination) return null;

    const pages = Array.from(pagination.querySelectorAll('.jdgm-paginate__page[data-page]'))
      .map((link) => Number.parseInt(link.getAttribute('data-page') || '0', 10))
      .filter((page) => Number.isFinite(page) && page > 0);
    const currentPage = Number.parseInt(
      pagination.querySelector('.jdgm-paginate__page.jdgm-curt')?.getAttribute('data-page') || '1',
      10
    );
    const perPage = Number.parseInt(pagination.getAttribute('data-per-page') || '5', 10);

    return {
      pagination,
      currentPage: Number.isFinite(currentPage) ? currentPage : 1,
      perPage: Number.isFinite(perPage) && perPage > 0 ? perPage : 5,
      maxPage: pages.length ? Math.max(...pages) : 1,
    };
  }

  function getReviewPageCacheKey(config, page, perPage, sortBy) {
    return [config.endpoint, config.productId, page, perPage, sortBy || 'default'].join('::');
  }

  async function fetchReviewPage(config, page, perPage, sortBy) {
    const url = new URL(config.endpoint, window.location.href);
    url.protocol = 'https:';
    url.searchParams.set('url', config.shopDomain);
    url.searchParams.set('shop_domain', config.shopDomain);
    url.searchParams.set('platform', window.jdgmSettings?.platform || 'shopify');
    url.searchParams.set('product_id', config.productId);
    url.searchParams.set('page', String(page));
    url.searchParams.set('per_page', String(perPage));
    if (sortBy) url.searchParams.set('sort_by', sortBy);
    if (config.updatedAtSeconds) url.searchParams.set('ts', String(config.updatedAtSeconds));

    const response = await fetch(url.toString(), {
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`Judge.me review page request failed: ${response.status}`);

    const payload = await response.json();
    if (!payload?.html) throw new Error('Judge.me review page response did not include HTML.');
    return payload.html;
  }

  function getReviewPageHtml(state, config, page, perPage, sortBy) {
    const key = getReviewPageCacheKey(config, page, perPage, sortBy);
    if (state.cache.has(key)) return state.cache.get(key);

    const request = fetchReviewPage(config, page, perPage, sortBy).catch((error) => {
      if (state.cache.get(key) === request) state.cache.delete(key);
      throw error;
    });
    state.cache.set(key, request);
    return request;
  }

  function parseReviewPageHtml(html) {
    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    const reviews = parsedDocument.querySelector('.jdgm-rev-widg__reviews');
    const pagination = parsedDocument.querySelector('.jdgm-paginate');
    if (!reviews || !pagination) {
      throw new Error('Judge.me review page markup was incomplete.');
    }
    return { reviews, pagination };
  }

  function cacheCurrentReviewPage(widget, state, config) {
    const details = getReviewPaginationDetails(widget);
    const reviews = widget.querySelector('.jdgm-rev-widg__reviews');
    if (!details || !reviews) return;

    const sortBy = getReviewSort(widget);
    const key = getReviewPageCacheKey(config, details.currentPage, details.perPage, sortBy);
    if (!state.cache.has(key)) {
      state.cache.set(key, Promise.resolve(`${reviews.outerHTML}${details.pagination.outerHTML}`));
    }
  }

  function prefetchAdjacentReviewPages(widget, state, config) {
    const details = getReviewPaginationDetails(widget);
    if (!details) return;

    const sortBy = getReviewSort(widget);
    [details.currentPage - 1, details.currentPage + 1]
      .filter((page) => page > 0 && page <= details.maxPage)
      .forEach((page) => {
        getReviewPageHtml(state, config, page, details.perPage, sortBy).catch(() => {});
      });
  }

  function announceReviewPage(root, message) {
    let status = root.querySelector('.drahance-jdgm-pagination-status');
    if (!status) {
      status = document.createElement('div');
      status.className = 'drahance-jdgm-pagination-status';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      root.appendChild(status);
    }
    status.textContent = message;
  }

  function setReviewPageLoading(root, widget, page, loading) {
    root.classList.toggle('drahance-jdgm-page-loading', loading);
    if (loading) {
      widget.setAttribute('aria-busy', 'true');
    } else {
      widget.removeAttribute('aria-busy');
    }

    const pagination = widget.querySelector('.jdgm-paginate');
    if (!pagination) return;
    pagination.classList.toggle('drahance-jdgm-pagination-loading', loading);
    pagination.querySelectorAll('.jdgm-paginate__page').forEach((link) => {
      if (loading) {
        link.setAttribute('aria-disabled', 'true');
      } else {
        link.removeAttribute('aria-disabled');
      }
      link.classList.toggle(
        'is-loading',
        loading && link.getAttribute('data-page') === String(page)
      );
    });
  }

  function scrollToReviewResults(widget) {
    const target = widget.querySelector('.drahance-jdgm-rendered-reviews');
    if (!target) return;

    const header = document.querySelector('.shopify-section--header, sticky-header, .header-wrapper');
    const headerStyle = header ? window.getComputedStyle(header) : null;
    const headerOffset = header && ['fixed', 'sticky'].includes(headerStyle?.position)
      ? header.getBoundingClientRect().height
      : 0;
    const top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - headerOffset - 16);
    window.scrollTo({ top, behavior: 'auto' });
  }

  async function goToReviewPage(root, page) {
    const widget = root.querySelector(WIDGET_SELECTOR);
    const config = widget ? getMediaRequestConfig(widget) : null;
    const details = widget ? getReviewPaginationDetails(widget) : null;
    if (!widget || !config || !details || page === details.currentPage) return;

    const state = getReviewPaginationState(root);
    if (state.loading) return;
    state.loading = true;
    setReviewPageLoading(root, widget, page, true);
    announceReviewPage(root, `Loading review page ${page}.`);

    try {
      const sortBy = getReviewSort(widget);
      const html = await getReviewPageHtml(state, config, page, details.perPage, sortBy);
      const nextPage = parseReviewPageHtml(html);
      const currentReviews = widget.querySelector('.jdgm-rev-widg__reviews');
      const currentPagination = widget.querySelector('.jdgm-paginate');
      if (!currentReviews || !currentPagination || !document.documentElement.contains(widget)) return;

      currentReviews.replaceWith(nextPage.reviews);
      currentPagination.replaceWith(nextPage.pagination);
      enhance(root);
      await new Promise((resolve) => window.requestAnimationFrame(resolve));

      const currentLink = widget.querySelector('.jdgm-paginate__page.jdgm-curt');
      currentLink?.focus({ preventScroll: true });
      scrollToReviewResults(widget);
      announceReviewPage(root, `Review page ${page} loaded.`);
      prefetchAdjacentReviewPages(widget, state, config);
    } catch (error) {
      console.warn('DRAHANCE review pagination kept the current page after a request failed.', error);
      announceReviewPage(root, 'Reviews could not be loaded. Please try again.');
    } finally {
      state.loading = false;
      setReviewPageLoading(root, widget, page, false);
    }
  }

  function getPaginationLink(root, event) {
    const target = event.target instanceof Element
      ? event.target.closest('.jdgm-paginate__page[data-page]')
      : null;
    return target && root.contains(target) ? target : null;
  }

  function interceptPaginationClick(root, event) {
    const link = getPaginationLink(root, event);
    if (!link || link.classList.contains('jdgm-curt')) return;

    const page = Number.parseInt(link.getAttribute('data-page') || '0', 10);
    const widget = root.querySelector(WIDGET_SELECTOR);
    if (!Number.isFinite(page) || page < 1 || !widget || !getMediaRequestConfig(widget)) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    goToReviewPage(root, page);
  }

  function interceptPaginationKeydown(root, event) {
    if (!['Enter', ' '].includes(event.key)) return;
    const link = getPaginationLink(root, event);
    if (!link || link.classList.contains('jdgm-curt')) return;

    event.preventDefault();
    event.stopPropagation();
    link.click();
  }

  function ensureProductMedia(root, widget, state) {
    if (state.loading || state.loaded) return;
    const config = getMediaRequestConfig(widget);
    if (!config) {
      state.loaded = true;
      return;
    }

    state.loading = true;
    fetchAllProductMedia(config)
      .then((items) => {
        mergeMediaItems(state, items);
      })
      .catch((error) => {
        state.failed = true;
        console.warn('DRAHANCE review media gallery fell back to the current review page.', error);
      })
      .finally(() => {
        state.loading = false;
        state.loaded = true;
        enhance(root);
      });
  }

  function createCameraIcon() {
    const icon = document.createElement('span');
    icon.className = 'drahance-jdgm-photos__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = [
      '<svg viewBox="0 0 24 24" focusable="false">',
      '<path d="M7.5 7.5 9 5h6l1.5 2.5H19a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9.5a2 2 0 0 1 2-2h2.5Z"/>',
      '<circle cx="12" cy="13.5" r="3.6"/>',
      '</svg>',
    ].join('');
    return icon;
  }

  function createPlayBadge() {
    const badge = document.createElement('span');
    badge.className = 'drahance-jdgm-media-play';
    badge.setAttribute('aria-hidden', 'true');
    badge.innerHTML = '<svg viewBox="0 0 24 24" focusable="false"><path d="m9 7 8 5-8 5V7Z"/></svg>';
    return badge;
  }

  function createMediaThumbnail(item, options = {}) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = options.className || 'drahance-jdgm-photos__tile';
    button.setAttribute('aria-label', options.label || `Open ${item.alt}`);

    if (item.thumbnail) {
      const image = document.createElement('img');
      image.src = item.thumbnail;
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      button.appendChild(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'drahance-jdgm-media-placeholder';
      placeholder.appendChild(createCameraIcon());
      button.appendChild(placeholder);
    }

    if (item.type === 'video') button.appendChild(createPlayBadge());
    if (options.onClick) button.addEventListener('click', options.onClick);
    return button;
  }

  function closeMediaModal() {
    if (!mediaModal || mediaModal.root.hidden) return;
    mediaModal.root.hidden = true;
    mediaModal.stage.innerHTML = '';
    document.body.style.overflow = mediaModal.bodyOverflow;
    mediaModal.lastFocused?.focus();
  }

  function renderMediaModal() {
    if (!mediaModal || !mediaModal.items.length) return;
    const item = mediaModal.items[mediaModal.index];
    mediaModal.stage.innerHTML = '';
    mediaModal.strip.innerHTML = '';
    mediaModal.counter.textContent = `${mediaModal.index + 1} / ${mediaModal.items.length}`;
    mediaModal.title.textContent = item.type === 'video' ? 'Customer review video' : 'Customer review photo';

    if (item.type === 'video' && item.iframeUrl) {
      const iframe = document.createElement('iframe');
      iframe.src = item.iframeUrl;
      iframe.title = item.alt;
      iframe.allow = 'autoplay; fullscreen; picture-in-picture';
      iframe.allowFullscreen = true;
      mediaModal.stage.appendChild(iframe);
    } else if (item.type === 'video' && item.videoUrl) {
      const video = document.createElement('video');
      video.src = item.videoUrl;
      video.controls = true;
      video.playsInline = true;
      video.preload = 'metadata';
      if (item.thumbnail) video.poster = item.thumbnail;
      mediaModal.stage.appendChild(video);
    } else {
      const image = document.createElement('img');
      image.src = item.url;
      image.alt = item.alt;
      mediaModal.stage.appendChild(image);
    }

    mediaModal.items.forEach((media, index) => {
      const thumbnail = createMediaThumbnail(media, {
        className: 'drahance-jdgm-media-modal__thumb',
        label: `Show ${media.type === 'video' ? 'video' : 'photo'} ${index + 1} of ${mediaModal.items.length}`,
        onClick: () => {
          mediaModal.index = index;
          renderMediaModal();
        },
      });
      if (index === mediaModal.index) {
        thumbnail.classList.add('is-current');
        thumbnail.setAttribute('aria-current', 'true');
      }
      mediaModal.strip.appendChild(thumbnail);
    });

    const hasMultiple = mediaModal.items.length > 1;
    mediaModal.previous.hidden = !hasMultiple;
    mediaModal.next.hidden = !hasMultiple;
    mediaModal.strip.hidden = !hasMultiple;
    window.requestAnimationFrame(() => {
      mediaModal.strip.querySelector('.is-current')?.scrollIntoView({ block: 'nearest', inline: 'center' });
    });
  }

  function stepMediaModal(direction) {
    if (!mediaModal || !mediaModal.items.length) return;
    mediaModal.index = (mediaModal.index + direction + mediaModal.items.length) % mediaModal.items.length;
    renderMediaModal();
  }

  function ensureMediaModal() {
    if (mediaModal) return mediaModal;

    const root = document.createElement('div');
    root.className = 'drahance-jdgm-media-modal';
    root.hidden = true;
    root.innerHTML = [
      '<div class="drahance-jdgm-media-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="drahance-review-media-title">',
      '<div class="drahance-jdgm-media-modal__header">',
      '<div><h3 id="drahance-review-media-title"></h3><span class="drahance-jdgm-media-modal__counter" aria-live="polite"></span></div>',
      '<button class="drahance-jdgm-media-modal__close" type="button" aria-label="Close media viewer">&times;</button>',
      '</div>',
      '<div class="drahance-jdgm-media-modal__viewer">',
      '<button class="drahance-jdgm-media-modal__arrow is-previous" type="button" aria-label="Previous media">&#8249;</button>',
      '<div class="drahance-jdgm-media-modal__stage"></div>',
      '<button class="drahance-jdgm-media-modal__arrow is-next" type="button" aria-label="Next media">&#8250;</button>',
      '</div>',
      '<div class="drahance-jdgm-media-modal__strip" aria-label="Customer review media thumbnails"></div>',
      '</div>',
    ].join('');

    document.body.appendChild(root);
    mediaModal = {
      root,
      dialog: root.querySelector('.drahance-jdgm-media-modal__dialog'),
      title: root.querySelector('h3'),
      counter: root.querySelector('.drahance-jdgm-media-modal__counter'),
      close: root.querySelector('.drahance-jdgm-media-modal__close'),
      previous: root.querySelector('.is-previous'),
      next: root.querySelector('.is-next'),
      stage: root.querySelector('.drahance-jdgm-media-modal__stage'),
      strip: root.querySelector('.drahance-jdgm-media-modal__strip'),
      items: [],
      index: 0,
      lastFocused: null,
      bodyOverflow: '',
    };

    mediaModal.close.addEventListener('click', closeMediaModal);
    mediaModal.previous.addEventListener('click', () => stepMediaModal(-1));
    mediaModal.next.addEventListener('click', () => stepMediaModal(1));
    root.addEventListener('click', (event) => {
      if (event.target === root) closeMediaModal();
    });
    document.addEventListener('keydown', (event) => {
      if (!mediaModal || mediaModal.root.hidden) return;
      if (event.key === 'Escape') closeMediaModal();
      if (event.key === 'ArrowLeft') stepMediaModal(-1);
      if (event.key === 'ArrowRight') stepMediaModal(1);
      if (event.key !== 'Tab') return;

      const focusable = Array.from(mediaModal.dialog.querySelectorAll('button:not([hidden])'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    return mediaModal;
  }

  function openMediaModal(items, index) {
    if (!items.length) return;
    const modal = ensureMediaModal();
    modal.items = items.slice();
    modal.index = Math.max(0, Math.min(index, modal.items.length - 1));
    modal.lastFocused = document.activeElement;
    modal.bodyOverflow = document.body.style.overflow;
    modal.root.hidden = false;
    document.body.style.overflow = 'hidden';
    renderMediaModal();
    modal.close.focus();
  }

  function createPhotos(state) {
    const photos = document.createElement('div');
    photos.className = 'drahance-jdgm-photos';

    const heading = document.createElement('div');
    heading.className = 'drahance-jdgm-photos__heading';
    const headingLabel = document.createElement('span');
    headingLabel.textContent = 'Customer Photos & Videos';
    const headingCount = document.createElement('span');
    headingCount.className = 'drahance-jdgm-photos__count';
    headingCount.textContent = state.loading
      ? 'Loading all…'
      : `${state.items.length} ${state.items.length === 1 ? 'item' : 'items'}`;
    heading.append(createCameraIcon(), headingLabel, headingCount);

    const grid = document.createElement('div');
    grid.className = 'drahance-jdgm-photos__grid';

    if (!state.items.length) {
      const empty = document.createElement('div');
      empty.className = 'drahance-jdgm-photos__empty';
      empty.append(createCameraIcon(), document.createTextNode(
        state.loading ? 'Collecting customer photos and videos…' : 'No customer photos or videos yet'
      ));
      grid.appendChild(empty);
    } else {
      state.items.slice(0, 6).forEach((item, index) => {
        const tile = createMediaThumbnail(item, {
          label: `Open ${item.type === 'video' ? 'video' : 'photo'} ${index + 1} of ${state.items.length}`,
          onClick: () => openMediaModal(state.items, index),
        });

        if (state.items.length > 6 && index === 5) {
          const more = document.createElement('span');
          more.className = 'drahance-jdgm-photos__more';
          more.textContent = `View all +${state.items.length - 5}`;
          tile.appendChild(more);
        }

        grid.appendChild(tile);
      });
    }

    photos.append(heading, grid);
    return photos;
  }

  function createReviewButton(widget) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'drahance-jdgm-write-button';
    button.textContent = 'Write a Review';
    button.addEventListener('click', () => {
      const original = widget.querySelector('.jdgm-write-rev-link');
      if (original) original.click();
    });
    return button;
  }

  function createTop(widget, state) {
    const average = widget.getAttribute('data-average-rating') || '0';
    const count = Number.parseInt(widget.getAttribute('data-number-of-reviews') || '0', 10);
    const top = document.createElement('div');
    top.className = 'drahance-jdgm-static-top';

    const titleRow = document.createElement('div');
    titleRow.className = 'drahance-jdgm-title-row';

    const title = document.createElement('h2');
    title.className = 'drahance-jdgm-static-title';
    title.textContent = 'Customer Reviews';

    titleRow.append(title, createReviewButton(widget));

    const layout = document.createElement('div');
    layout.className = 'drahance-jdgm-summary-layout';

    const ratingPanel = document.createElement('div');
    ratingPanel.className = 'drahance-jdgm-rating-panel';

    const rating = document.createElement('div');
    rating.className = 'drahance-jdgm-average';
    rating.textContent = formatRating(average);

    const based = document.createElement('div');
    based.className = 'drahance-jdgm-based-on';
    based.textContent = `Based on ${Number.isFinite(count) ? count : 0} ${count === 1 ? 'review' : 'reviews'}`;

    ratingPanel.append(rating, createStars(average), based, createHistogram(widget));
    layout.append(ratingPanel, createPhotos(state));
    top.append(titleRow, layout);

    return top;
  }

  function syncEmptyState(widget) {
    const body = widget.querySelector('.jdgm-rev-widg__body');
    if (!body) return;
    const count = Number.parseInt(widget.getAttribute('data-number-of-reviews') || '0', 10);
    const hasReview = Boolean(widget.querySelector('.jdgm-rev'));
    let empty = widget.querySelector('.drahance-jdgm-empty-reviews');

    if (count > 0 || hasReview) {
      if (empty) empty.remove();
      return;
    }

    if (!empty) {
      empty = document.createElement('div');
      empty.className = 'drahance-jdgm-empty-reviews';
      empty.innerHTML = '<p class="drahance-jdgm-empty-reviews__title">No reviews yet.</p><p class="drahance-jdgm-empty-reviews__text">Be the first to share your experience.</p>';
      body.prepend(empty);
    }
  }

  function syncReviewInitials(widget) {
    widget.querySelectorAll('.jdgm-rev').forEach((review) => {
      const icon = review.querySelector('.jdgm-rev__icon');
      const author = review.querySelector('.jdgm-rev__author');
      if (!icon || !author) return;
      const name = author.textContent.trim();
      if (!name) return;
      icon.textContent = name.charAt(0).toUpperCase();
      icon.setAttribute('aria-label', `${name} avatar`);
    });
  }

  function getReviewDate(review) {
    const timestamp = review.querySelector('.jdgm-rev__timestamp');
    const text = timestamp ? timestamp.textContent.trim() : '';
    if (text) return text;

    const raw = timestamp ? (timestamp.getAttribute('data-content') || timestamp.getAttribute('datetime') || '') : '';
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return match ? `${match[2]}/${match[3]}/${match[1]}` : raw.trim();
  }

  function createRenderedStars(score) {
    const stars = document.createElement('div');
    stars.className = 'drahance-jdgm-rendered-review__stars';
    stars.setAttribute('aria-label', `${formatRating(score)} out of 5 stars`);

    const rounded = Math.round(toNumber(score));
    for (let index = 1; index <= 5; index += 1) {
      const star = document.createElement('span');
      star.className = index <= rounded ? 'is-filled' : 'is-empty';
      star.textContent = String.fromCharCode(9733);
      stars.appendChild(star);
    }

    return stars;
  }

  function getReviewSignature(reviews) {
    return reviews.map((review) => {
      const id = review.getAttribute('data-review-id') || review.id || '';
      const author = review.querySelector('.jdgm-rev__author')?.textContent.trim() || '';
      const date = getReviewDate(review);
      const body = review.querySelector('.jdgm-rev__body')?.textContent.trim() || '';
      const media = getMediaItems(review).map((item) => item.key).join(',');
      return [id, author, date, body.length, media].join(':');
    }).join('|');
  }

  function renderReviewList(widget, state) {
    const body = widget.querySelector('.jdgm-rev-widg__body');
    const sourceList = widget.querySelector('.jdgm-rev-widg__reviews');
    if (!body || !sourceList) return;

    const sourceReviews = Array.from(sourceList.children).filter((node) => node.classList.contains('jdgm-rev'));
    let rendered = body.querySelector('.drahance-jdgm-rendered-reviews');

    if (!sourceReviews.length) {
      if (rendered) rendered.remove();
      widget.closest(ROOT_SELECTOR)?.classList.remove('drahance-judgeme-rendered');
      return;
    }

    const signature = getReviewSignature(sourceReviews);
    if (rendered && rendered.dataset.signature === signature) {
      widget.closest(ROOT_SELECTOR)?.classList.add('drahance-judgeme-rendered');
      return;
    }

    if (!rendered) {
      rendered = document.createElement('div');
      rendered.className = 'drahance-jdgm-rendered-reviews';
    }

    rendered.dataset.signature = signature;
    rendered.innerHTML = '';

    sourceReviews.forEach((review) => {
      const author = review.querySelector('.jdgm-rev__author')?.textContent.trim() || 'Customer';
      const score = review.querySelector('.jdgm-rev__rating')?.getAttribute('data-score') || '5';
      const bodyNode = review.querySelector('.jdgm-rev__body');
      const reviewMedia = getMediaItems(review);

      const card = document.createElement('article');
      card.className = 'drahance-jdgm-rendered-review';

      const avatar = document.createElement('div');
      avatar.className = 'drahance-jdgm-rendered-review__avatar';
      avatar.textContent = author.charAt(0).toUpperCase();
      avatar.setAttribute('aria-label', `${author} avatar`);

      const meta = document.createElement('div');
      meta.className = 'drahance-jdgm-rendered-review__meta';

      const name = document.createElement('div');
      name.className = 'drahance-jdgm-rendered-review__name';
      name.textContent = author;
      meta.appendChild(name);

      const date = document.createElement('time');
      date.className = 'drahance-jdgm-rendered-review__date';
      date.textContent = getReviewDate(review);

      const copy = document.createElement('div');
      copy.className = 'drahance-jdgm-rendered-review__body';
      copy.innerHTML = bodyNode ? bodyNode.innerHTML : '';

      card.append(avatar, meta, date, createRenderedStars(score), copy);

      if (reviewMedia.length) {
        const media = document.createElement('div');
        media.className = 'drahance-jdgm-rendered-review__media';
        reviewMedia.forEach((item, reviewMediaIndex) => {
          const thumbnail = createMediaThumbnail(item, {
            className: 'drahance-jdgm-rendered-review__media-button',
            label: `Open this review's ${item.type === 'video' ? 'video' : 'photo'} ${reviewMediaIndex + 1} of ${reviewMedia.length}`,
            onClick: () => {
              const itemIndex = state.items.findIndex((candidate) => candidate.key === item.key);
              openMediaModal(state.items.length ? state.items : reviewMedia, itemIndex >= 0 ? itemIndex : reviewMediaIndex);
            },
          });
          media.appendChild(thumbnail);
        });
        card.appendChild(media);
      }

      rendered.appendChild(card);
    });

    const readMoreBar = body.querySelector('.drahance-jdgm-read-more-bar');
    if (readMoreBar) {
      readMoreBar.after(rendered);
    } else {
      body.prepend(rendered);
    }

    widget.closest(ROOT_SELECTOR)?.classList.add('drahance-judgeme-rendered');
  }

  function syncReadMore(widget) {
    widget.querySelectorAll('.jdgm-rev .drahance-jdgm-read-more').forEach((link) => link.remove());

    const body = widget.querySelector('.jdgm-rev-widg__body');
    if (!body || body.querySelector('.drahance-jdgm-read-more-bar')) return;

    const bar = document.createElement('div');
    bar.className = 'drahance-jdgm-read-more-bar';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'drahance-jdgm-read-more';
    button.textContent = 'Read More';
    button.addEventListener('click', () => {
      widget.classList.toggle('drahance-jdgm-show-all-reviews');
      const expanded = widget.classList.contains('drahance-jdgm-show-all-reviews');
      button.textContent = expanded ? 'Show Less' : 'Read More';
    });

    bar.appendChild(button);
    body.prepend(bar);
  }

  function enhance(root) {
    const widget = root.querySelector(WIDGET_SELECTOR);
    if (!widget) return;
    const state = getMediaState(root, widget);
    ensureProductMedia(root, widget, state);

    const signature = [
      widget.getAttribute('data-average-rating') || '0',
      widget.getAttribute('data-number-of-reviews') || '0',
      state.loading ? 'loading' : 'ready',
      state.items.map((item) => item.key).join('|'),
    ].join('::');

    let top = root.querySelector('.drahance-jdgm-static-top');
    if (!top || top.dataset.signature !== signature) {
      const nextTop = createTop(widget, state);
      nextTop.dataset.signature = signature;
      if (top) {
        top.replaceWith(nextTop);
      } else {
        widget.prepend(nextTop);
      }
    }

    syncEmptyState(widget);
    syncReviewInitials(widget);
    syncReadMore(widget);
    renderReviewList(widget, state);
    const paginationState = getReviewPaginationState(root);
    const paginationConfig = getMediaRequestConfig(widget);
    if (paginationConfig) {
      cacheCurrentReviewPage(widget, paginationState, paginationConfig);
      prefetchAdjacentReviewPages(widget, paginationState, paginationConfig);
    }
    root.classList.add('drahance-judgeme-ready');
  }

  function initRoot(root) {
    let scheduled = false;
    const run = () => {
      scheduled = false;
      enhance(root);
    };

    enhance(root);

    root.addEventListener('click', (event) => interceptPaginationClick(root, event), true);
    root.addEventListener('keydown', (event) => interceptPaginationKeydown(root, event), true);

    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(run);
    });

    observer.observe(root, { childList: true, subtree: true });
  }

  function init() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
